import React, { useEffect, useState } from 'react';
import { apiCall } from '../utils/api';

interface QueueItem {
  id: number;
  song_id: number;
  user_name: string;
  queue_order: number;
  status: string;
  title: string;
  artist: string;
  genre: string;
}

interface PlayedSong {
  history_id: number;
  song_id: number;
  user_name: string;
  status: string;
  gig_session: string | null;
  added_at: string;
  played_at: string | null;
  completed_at: string | null;
  title: string;
  artist: string;
  genre: string;
  average_rating: number;
  vote_count: number;
}

interface Vote {
  id: number;
  voter_name: string;
  rating: number;
  comment: string | null;
  created_at: string;
}

const QueueAndVotingPage: React.FC = () => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [playedSongs, setPlayedSongs] = useState<PlayedSong[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [votingFor, setVotingFor] = useState<number | null>(null);
  const [voterName, setVoterName] = useState('');
  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState('');
  const [submittingVote, setSubmittingVote] = useState(false);
  const [votes, setVotes] = useState<Record<number, Vote[]>>({});
  const [userVotes, setUserVotes] = useState<Record<number, Vote | null>>({}); // Track if current user has voted
  const [currentVoterName, setCurrentVoterName] = useState(() => localStorage.getItem('voterName') || '');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchData();
    // Poll every 5 seconds to keep data fresh
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      // Fetch queue
      const queueRes = await apiCall('/api/songs/queue');
      if (queueRes.ok) {
        const queueData = await queueRes.json();
        setQueue(queueData);
      }

      // Fetch played songs
      const playedRes = await apiCall('/api/songs/played?limit=50');
      if (playedRes.ok) {
        const playedData = await playedRes.json();
        setPlayedSongs(playedData);

        // Fetch votes for each played song
        for (const song of playedData) {
          if (!votes[song.history_id]) {
            fetchVotes(song.history_id);
          }
          // Check if current user has voted (if voter name is set)
          if (currentVoterName && !userVotes[song.history_id]) {
            checkUserVote(song.history_id);
          }
        }
      }

      setLoading(false);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
      setLoading(false);
    }
  };

  const fetchVotes = async (historyId: number) => {
    try {
      const res = await apiCall(`/api/songs/votes/${historyId}`);
      if (res.ok) {
        const votesData = await res.json();
        setVotes(prev => ({ ...prev, [historyId]: votesData }));
      }
    } catch (err) {
      // Silently fail for votes
    }
  };

  const checkUserVote = async (historyId: number) => {
    if (!currentVoterName) return;
    try {
      const res = await apiCall(`/api/songs/votes/${historyId}/check/${encodeURIComponent(currentVoterName)}`);
      if (res.ok) {
        const data = await res.json();
        setUserVotes(prev => ({ ...prev, [historyId]: data.hasVoted ? data.vote : null }));
      }
    } catch (err) {
      // Silently fail
    }
  };

  const handleVoteClick = (historyId: number) => {
    setVotingFor(historyId);
    // Pre-populate with existing vote if user has already voted
    const existingVote = userVotes[historyId];
    if (existingVote) {
      setVoterName(currentVoterName);
      setRating(existingVote.rating);
      setComment(existingVote.comment || '');
    } else {
      setVoterName(currentVoterName || '');
      setRating(5);
      setComment('');
    }
    setMessage(null);
  };

  const handleSubmitVote = async () => {
    if (!votingFor || !voterName.trim()) {
      setMessage({ type: 'error', text: 'Please enter your name' });
      return;
    }

    setSubmittingVote(true);
    setMessage(null);

    try {
      const res = await apiCall('/api/songs/votes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          song_history_id: votingFor,
          voter_name: voterName.trim(),
          rating,
          comment: comment.trim() || null
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to submit vote');
      }

      setMessage({ type: 'success', text: 'Vote submitted successfully!' });

      // Save voter name to localStorage for future sessions
      if (voterName.trim()) {
        localStorage.setItem('voterName', voterName.trim());
        setCurrentVoterName(voterName.trim());
      }

      setVotingFor(null);
      setVoterName('');
      setRating(5);
      setComment('');

      // Refresh votes and played songs
      if (votingFor) {
        fetchVotes(votingFor);
        checkUserVote(votingFor);
      }
      fetchData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to submit vote' });
    } finally {
      setSubmittingVote(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const renderStars = (rating: number) => {
    return '★'.repeat(Math.round(rating)) + '☆'.repeat(5 - Math.round(rating));
  };

  return (
    <div className="container" style={{ padding: '20px' }}>
      <h1 style={{
        color: '#ffd700',
        textShadow: '0 0 10px rgba(255, 215, 0, 0.3)',
        borderBottom: '2px solid #5a3a1a',
        paddingBottom: '12px',
        marginBottom: '24px',
        letterSpacing: '1px'
      }}>
        Queue & Performance Voting
      </h1>

      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          backgroundColor: message.type === 'success' ? 'rgba(46,204,113,0.15)' : 'rgba(231,76,60,0.15)',
          color: message.type === 'success' ? '#2ecc71' : '#e74c3c',
          border: `1px solid ${message.type === 'success' ? 'rgba(46,204,113,0.3)' : 'rgba(231,76,60,0.3)'}`,
          borderRadius: '6px'
        }}>
          {message.text}
          <button
            onClick={() => setMessage(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '16px' }}
          >
            x
          </button>
        </div>
      )}

      {loading && <p style={{ color: '#a08060' }}>Loading...</p>}
      {error && <p style={{ color: '#e74c3c' }}>{error}</p>}

      {/* Current Queue Section */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{
          color: '#f5deb3',
          borderBottom: '1px solid #3a2510',
          paddingBottom: '8px',
          marginBottom: '16px'
        }}>
          Current Queue
        </h2>
        {queue.length === 0 ? (
          <p style={{ color: '#806040', fontStyle: 'italic' }}>No songs in the queue</p>
        ) : (
          <div style={{
            overflowX: 'auto',
            border: '1px solid #5a3a1a',
            borderRadius: '8px',
            backgroundColor: 'rgba(40,25,15,0.95)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'rgba(90,58,26,0.6)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a', fontWeight: 600, letterSpacing: '0.5px' }}>#</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a', fontWeight: 600, letterSpacing: '0.5px' }}>Title</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a', fontWeight: 600, letterSpacing: '0.5px' }}>Artist</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a', fontWeight: 600, letterSpacing: '0.5px' }}>Performer</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#ffd700', borderBottom: '2px solid #5a3a1a', fontWeight: 600, letterSpacing: '0.5px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((item, idx) => (
                  <tr key={item.id} style={{
                    backgroundColor: idx % 2 === 0 ? 'rgba(40,25,15,0.95)' : 'rgba(50,32,18,0.95)',
                    borderBottom: '1px solid #3a2510'
                  }}>
                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#c8a060' }}>{item.queue_order}</td>
                    <td style={{ padding: '10px', color: '#f5deb3' }}>{item.title}</td>
                    <td style={{ padding: '10px', color: '#a08060' }}>{item.artist}</td>
                    <td style={{ padding: '10px', color: '#f5deb3' }}>{item.user_name}</td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        backgroundColor: item.status === 'playing' ? 'rgba(255,215,0,0.2)' : 'rgba(90,58,26,0.5)',
                        color: item.status === 'playing' ? '#ffd700' : '#a08060',
                        border: item.status === 'playing' ? '1px solid rgba(255,215,0,0.4)' : '1px solid #5a3a1a',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        letterSpacing: '0.5px',
                        textShadow: item.status === 'playing' ? '0 0 6px rgba(255,215,0,0.3)' : 'none'
                      }}>
                        {item.status === 'playing' ? 'NOW PLAYING' : 'QUEUED'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Played Songs & Voting Section */}
      <div>
        <h2 style={{
          color: '#f5deb3',
          borderBottom: '1px solid #3a2510',
          paddingBottom: '8px',
          marginBottom: '16px'
        }}>
          Past Performances
        </h2>
        {playedSongs.length === 0 ? (
          <p style={{ color: '#806040', fontStyle: 'italic' }}>No performances yet</p>
        ) : (
          <div style={{ display: 'grid', gap: '20px' }}>
            {playedSongs.map((song) => (
              <div
                key={song.history_id}
                style={{
                  border: '1px solid #5a3a1a',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: 'rgba(40,25,15,0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', color: '#f5deb3' }}>{song.title}</h3>
                    <p style={{ margin: '0 0 5px 0', color: '#a08060' }}>{song.artist}</p>
                    <p style={{ margin: '0', fontSize: '14px', color: '#806040' }}>
                      Performed by <strong style={{ color: '#c8a060' }}>{song.user_name}</strong> -- {formatDate(song.completed_at || song.played_at)}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                      <span style={{ color: '#ffd700', textShadow: '0 0 4px rgba(255,215,0,0.3)' }}>
                        {'★'.repeat(Math.round(song.average_rating))}
                      </span>
                      <span style={{ color: '#5a3a1a' }}>
                        {'☆'.repeat(5 - Math.round(song.average_rating))}
                      </span>
                    </div>
                    <p style={{ margin: '0', fontSize: '12px', color: '#806040' }}>
                      {song.vote_count} {song.vote_count === 1 ? 'vote' : 'votes'}
                    </p>
                  </div>
                </div>

                {/* Voting Form */}
                {votingFor === song.history_id ? (
                  <div style={{
                    borderTop: '1px solid #3a2510',
                    paddingTop: '15px',
                    marginTop: '15px'
                  }}>
                    <div style={{ display: 'grid', gap: '10px', maxWidth: '500px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#f5deb3' }}>
                          Your Name *
                        </label>
                        <input
                          type="text"
                          value={voterName}
                          onChange={e => setVoterName(e.target.value)}
                          placeholder="Enter your name"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '14px',
                            backgroundColor: '#28190f',
                            border: '1px solid #5a3a1a',
                            borderRadius: '4px',
                            color: '#f5deb3',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                          disabled={submittingVote}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#f5deb3' }}>
                          Rating: {rating} {rating === 1 ? 'star' : 'stars'}
                        </label>
                        <div style={{ display: 'flex', gap: '5px', fontSize: '24px', cursor: 'pointer' }}>
                          {[1, 2, 3, 4, 5].map(star => (
                            <span
                              key={star}
                              onClick={() => !submittingVote && setRating(star)}
                              style={{
                                color: star <= rating ? '#ffd700' : '#5a3a1a',
                                cursor: submittingVote ? 'default' : 'pointer',
                                textShadow: star <= rating ? '0 0 6px rgba(255,215,0,0.3)' : 'none'
                              }}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#f5deb3' }}>
                          Comment (optional)
                        </label>
                        <textarea
                          value={comment}
                          onChange={e => setComment(e.target.value)}
                          placeholder="Share your thoughts..."
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '14px',
                            minHeight: '80px',
                            backgroundColor: '#28190f',
                            border: '1px solid #5a3a1a',
                            borderRadius: '4px',
                            color: '#f5deb3',
                            outline: 'none',
                            resize: 'vertical',
                            boxSizing: 'border-box'
                          }}
                          disabled={submittingVote}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={handleSubmitVote}
                          disabled={submittingVote || !voterName.trim()}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: submittingVote || !voterName.trim() ? '#3a2510' : '#c8850f',
                            color: submittingVote || !voterName.trim() ? '#806040' : '#28190f',
                            border: '1px solid #5a3a1a',
                            borderRadius: '4px',
                            cursor: submittingVote || !voterName.trim() ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {submittingVote ? 'Submitting...' : 'Submit Vote'}
                        </button>
                        <button
                          onClick={() => {
                            setVotingFor(null);
                            setVoterName('');
                            setRating(5);
                            setComment('');
                          }}
                          disabled={submittingVote}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: 'transparent',
                            color: '#a08060',
                            border: '1px solid #5a3a1a',
                            borderRadius: '4px',
                            cursor: submittingVote ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {userVotes[song.history_id] ? (
                      <div style={{
                        padding: '8px 16px',
                        backgroundColor: 'rgba(90,58,26,0.4)',
                        color: '#a08060',
                        borderRadius: '4px',
                        fontSize: '14px',
                        border: '1px solid #5a3a1a'
                      }}>
                        You've already voted (
                        <span style={{ color: '#ffd700' }}>
                          {'★'.repeat(Math.round(userVotes[song.history_id]!.rating))}
                        </span>
                        <span style={{ color: '#5a3a1a' }}>
                          {'☆'.repeat(5 - Math.round(userVotes[song.history_id]!.rating))}
                        </span>
                        )
                      </div>
                    ) : null}
                    <button
                      onClick={() => handleVoteClick(song.history_id)}
                      style={{
                        padding: '12px 20px',
                        backgroundColor: userVotes[song.history_id] ? 'transparent' : '#c8850f',
                        color: userVotes[song.history_id] ? '#a08060' : '#28190f',
                        border: userVotes[song.history_id] ? '1px solid #5a3a1a' : '1px solid #c8850f',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: userVotes[song.history_id] ? 'normal' : 'bold'
                      }}
                    >
                      {userVotes[song.history_id] ? 'Update Your Vote' : 'Vote on This Performance'}
                    </button>
                    {votes[song.history_id] && votes[song.history_id].length > 0 && (
                      <button
                        onClick={() => fetchVotes(song.history_id)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: 'transparent',
                          color: '#a08060',
                          border: '1px solid #5a3a1a',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        View Votes ({votes[song.history_id].length})
                      </button>
                    )}
                  </div>
                )}

                {/* Display Votes */}
                {votes[song.history_id] && votes[song.history_id].length > 0 && votingFor !== song.history_id && (
                  <div style={{
                    borderTop: '1px solid #3a2510',
                    paddingTop: '15px',
                    marginTop: '15px'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#c8a060' }}>Recent Votes:</h4>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {votes[song.history_id].slice(0, 5).map((vote) => (
                        <div key={vote.id} style={{
                          padding: '10px 12px',
                          backgroundColor: 'rgba(50,32,18,0.8)',
                          borderRadius: '4px',
                          borderLeft: '3px solid #c8850f'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <strong style={{ color: '#f5deb3' }}>{vote.voter_name}</strong>
                            <span>
                              <span style={{ color: '#ffd700' }}>{'★'.repeat(Math.round(vote.rating))}</span>
                              <span style={{ color: '#5a3a1a' }}>{'☆'.repeat(5 - Math.round(vote.rating))}</span>
                            </span>
                          </div>
                          {vote.comment && (
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#a08060' }}>{vote.comment}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QueueAndVotingPage;
